import type { ClientEnvironment } from '@polyai/common/constants/global.constants';
import { ENDPOINTS as STREAM_ENDPOINTS } from '@polyai/stream/endpoints';

import ENDPOINTS from 'api/endpoints';
import { doGet, doPost } from 'api/request';
import { LOGGER_TYPE } from 'api/types';
import { generateNextPath } from 'lib/routeHelper';
import type { GetProjectionResponse } from '../projection';
import type {
  ActiveDeploymentsResponse,
  ArtifactDeployment,
  DeploymentsResponse,
  ProjectVersionState,
  RollbackDeploymentStreamResponse,
} from './types';

export const getDeployments = (
  accountId: string,
  projectId: string,
  clientEnv: ClientEnvironment,
) => {
  return doGet<DeploymentsResponse>(
    generateNextPath(ENDPOINTS.DEPLOYMENTS, {
      accountId,
      projectId,
    }),
    LOGGER_TYPE.JUPITER,
    {
      version: 'v2',
      params: {
        client_env: clientEnv,
      },
    },
  );
};

export const initialSandboxDeployment = ({
  accountId,
  projectId,
  deploymentMessage,
}: {
  accountId: string;
  projectId: string;
  deploymentMessage: string;
}) => {
  return doPost<{
    deploymentId: string;
    artifactVersion: string;
    functionDeploymentId: string | null;
    lambdaDeploymentVersion: string | null;
  }>(
    generateNextPath(ENDPOINTS.DEPLOYMENTS_INITIAL, {
      accountId,
      projectId,
    }),
    LOGGER_TYPE.JUPITER,
    {
      deployment_message: deploymentMessage,
    },
    { version: 'v1', prefix: 'sourcerer' },
  );
};

type PromoteDeploymentBody = {
  deployment_message: string;
  client_env?: ClientEnvironment;
};

export const promoteDeployment = ({
  accountId,
  projectId,
  deploymentId,
  deploymentMessage,
  newClientEnv,
  useSourcerer,
}: {
  accountId: string;
  projectId: string;
  deploymentId: string;
  deploymentMessage: string;
  newClientEnv?: ClientEnvironment;
  useSourcerer?: boolean;
}) => {
  let body: PromoteDeploymentBody = {
    deployment_message: deploymentMessage,
  };
  if (newClientEnv) {
    body = {
      ...body,
      client_env: newClientEnv,
    };
  }
  return doPost<ArtifactDeployment>(
    generateNextPath(ENDPOINTS.PROMOTE_DEPLOYMENT, {
      accountId,
      projectId,
      deploymentId,
    }),
    useSourcerer ? LOGGER_TYPE.AGENT_STREAM : LOGGER_TYPE.JUPITER,
    body,
    {
      version: useSourcerer ? 'v1' : 'v2',
      prefix: useSourcerer ? 'sourcerer' : undefined,
    },
  );
};

export const rollbackDeployment = ({
  accountId,
  projectId,
  deploymentId,
  deploymentMessage,
}: {
  accountId: string;
  projectId: string;
  deploymentId: string;
  deploymentMessage: string;
}) => {
  return doPost<ArtifactDeployment>(
    generateNextPath(ENDPOINTS.ROLLBACK_DEPLOYMENT, {
      accountId,
      projectId,
      deploymentId,
    }),
    LOGGER_TYPE.JUPITER,
    {
      deployment_message: deploymentMessage,
    },
    { version: 'v2' },
  );
};

export const rollbackDeploymentStream = ({
  accountId,
  projectId,
  deploymentId,
  deploymentMessage,
}: {
  accountId: string;
  projectId: string;
  deploymentId: string;
  deploymentMessage: string;
}) => {
  return doPost<RollbackDeploymentStreamResponse>(
    generateNextPath(ENDPOINTS.ROLLBACK_DEPLOYMENT, {
      accountId,
      projectId,
      deploymentId,
    }),
    LOGGER_TYPE.JUPITER,
    {
      deployment_message: deploymentMessage,
    },
    {
      version: 'v1',
      prefix: 'sourcerer',
    },
  );
};

export const getActiveDeployments = (accountId: string, projectId: string) => {
  return doGet<ActiveDeploymentsResponse>(
    generateNextPath(ENDPOINTS.ACTIVE_DEPLOYMENTS, {
      accountId,
      projectId,
    }),
    LOGGER_TYPE.JUPITER,
    {
      version: 'v2',
    },
  );
};

// TODO: delete after architecture migration
export const getCurrentState = (
  accountId: string,
  projectId: string,
  deploymentId: string,
) => {
  return doGet<ProjectVersionState>(
    generateNextPath(ENDPOINTS.DEPLOYMENT_STATE, {
      accountId,
      projectId,
      deploymentId,
    }),
    LOGGER_TYPE.JUPITER,
    {
      version: 'v2',
    },
  );
};

export const getDeploymentProjection = ({
  accountId,
  projectId,
  deploymentId = '',
}: {
  accountId: string;
  projectId: string;
  deploymentId?: string;
}) => {
  return doGet<GetProjectionResponse>(
    generateNextPath(STREAM_ENDPOINTS.DEPLOYMENT_PROJECTION, {
      accountId,
      projectId,
      deploymentId,
    }),
    LOGGER_TYPE.JUPITER,
    {
      prefix: 'sourcerer',
    },
  );
};
